import { motion } from "framer-motion";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const steps = [
  {
    num: "01",
    title: "Anfragen",
    text: "Nehmen Sie Kontakt auf – per Telefon oder über unser Kontaktformular. Wir beraten Sie gerne zu Ihrem Event.",
  },
  {
    num: "02",
    title: "Besichtigung",
    text: "Erleben Sie den Künstlerhof live. Wir zeigen Ihnen die Scheune, das Außengelände und besprechen Ihre Wünsche vor Ort.",
  },
  {
    num: "03",
    title: "Buchung",
    text: "Wir bestätigen Ihren Wunschtermin und schließen den Mietvertrag ab. Einfach und unkompliziert.",
  },
  {
    num: "04",
    title: "Feiern!",
    text: "Sie richten nach Ihren Vorstellungen ein und feiern unvergesslich. Das Atelier 77 gehört Ihnen.",
  },
];

const included = [
  "Partyscheune ca. 220 qm",
  "Theke mit Spüle",
  "Bestuhlung & Tische",
  "Außengelände nutzbar",
  "Parkplätze",
  "Schlüsselübergabe",
];

const notIncluded = [
  "Catering / Getränke",
  "Dekoration (in Eigenregie – volle Freiheit!)",
  "Ton- & Lichttechnik (nach Absprache)",
  "DJ / Musik (selbst organisieren)",
];

const cities = ["Bochum", "Dortmund", "Herne", "Essen", "Gelsenkirchen", "Witten", "Hattingen", "Recklinghausen"];

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

export default function Pricing() {
  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      data-testid="pricing-page"
    >
      <div className="pt-32 pb-12 px-6 md:px-12 max-w-7xl mx-auto text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Transparent & Fair
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="text-5xl md:text-7xl font-extrabold uppercase text-[#F2EDE8]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="pricing-title"
        >
          Preise & Buchung
        </motion.h1>
      </div>

      {/* Price Cards */}
      <section className="py-8 px-6 md:px-12 max-w-7xl mx-auto" data-testid="price-section">
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-[#1C1C1E] border border-[#C1440E]/30 p-8 md:p-10 relative overflow-hidden"
            data-testid="price-card"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C1440E]/5 rounded-bl-full" />
            <p
              className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-5"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Mietpreis
            </p>
            <div className="flex items-baseline gap-2 mb-1">
              <span
                className="text-6xl md:text-8xl font-extrabold text-[#C1440E] leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="price-amount"
              >
                600 €
              </span>
            </div>
            <p
              className="text-[#A19D98] text-sm mb-8"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              ab (inkl. MwSt.) · Tagesmiete
            </p>
            <div className="space-y-3 mb-8">
              <p
                className="text-[#D4A843] text-xs tracking-widest uppercase"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Im Preis enthalten:
              </p>
              {included.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle2 size={13} className="text-[#C1440E] flex-shrink-0" />
                  <span
                    className="text-[#F2EDE8]/75 text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#F2EDE8]/10 pt-6 space-y-2.5">
              <p
                className="text-[#A19D98] text-xs tracking-widest uppercase"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Nicht enthalten:
              </p>
              {notIncluded.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-1 h-1 rounded-full bg-[#A19D98] flex-shrink-0" />
                  <span
                    className="text-[#A19D98] text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="flex flex-col gap-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-[#1C1C1E] p-8 border border-[#D4A843]/20 flex-1"
            >
              <p
                className="text-[#D4A843] text-xs tracking-widest uppercase mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Volle Freiheit
              </p>
              <h3
                className="text-2xl font-light text-[#F2EDE8] mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Dekoration in Eigenregie
              </h3>
              <p
                className="text-[#A19D98] text-sm leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Im Atelier 77 sind Sie der Gestalter. Die Location gibt den Rahmen – was Sie daraus
                machen, liegt ganz bei Ihnen. Keine vorgeschriebenen Dekopakete, keine
                Cateringpflicht. Nur Ihr Event, Ihre Ideen, Ihr Stil.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-[#1C1C1E] p-8 border border-[#F2EDE8]/5 flex-1"
            >
              <p
                className="text-[#C1440E] text-xs tracking-widest uppercase mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Einzugsgebiet
              </p>
              <p
                className="text-[#F2EDE8]/70 text-sm leading-relaxed mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Wir bedienen das gesamte Ruhrgebiet:
              </p>
              <div className="flex flex-wrap gap-2">
                {cities.map((city) => (
                  <span
                    key={city}
                    className="px-2 py-1 bg-[#141414] text-[#A19D98] text-xs border border-[#F2EDE8]/5"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {city}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking Steps */}
      <section className="py-24 bg-[#1C1C1E] mt-12" data-testid="booking-steps">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <p
              className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              So einfach geht's
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-[#F2EDE8]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              In vier Schritten zur Feier
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="relative"
                data-testid={`booking-step-${i}`}
              >
                <span
                  className="text-5xl md:text-6xl font-extrabold block mb-3 leading-none"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", color: "rgba(193,68,14,0.2)" }}
                >
                  {step.num}
                </span>
                <h3
                  className="text-xl font-semibold text-[#F2EDE8] mb-3"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-[#A19D98] text-sm leading-relaxed"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {step.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 text-center" data-testid="pricing-cta">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2
            className="text-3xl md:text-5xl font-light text-[#F2EDE8] mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Bereit für Ihr Event?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:02349350002"
              className="flex items-center justify-center gap-3 bg-[#C1440E] hover:bg-[#D4A843] text-white hover:text-[#141414] px-8 py-4 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: "13px", letterSpacing: "0.15em", textTransform: "uppercase" }}
              data-testid="pricing-phone-cta"
            >
              <Phone size={16} />
              0234 / 935 000 2
            </a>
            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 border border-[#C1440E] text-[#C1440E] hover:bg-[#C1440E] hover:text-white px-8 py-4 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: "13px", letterSpacing: "0.15em", textTransform: "uppercase" }}
              data-testid="pricing-form-cta"
            >
              Online Anfragen <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </section>
    </motion.main>
  );
}
